import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me — AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Step 1: password check → returns either OTP challenge or (trusted device) full auth
    const { data } = await api.post("/auth/login", { email, password });
    if (data.otp_required) return data; // caller must complete OTP
    // Trusted device skipped OTP — treat as full login
    if (data.token) {
      localStorage.setItem("axiony_token", data.token);
      setUser(data.user);
      return { ...data.user, trusted_device_skip: !!data.trusted_device };
    }
    return data;
  };

  const verifyOtp = async (otp_session_id, code, trust_device = false) => {
    const { data } = await api.post("/auth/login/verify", { otp_session_id, code, trust_device });
    localStorage.setItem("axiony_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (otp_session_id) => {
    const { data } = await api.post("/auth/login/resend", { otp_session_id, code: "" });
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/password/forgot", { email });
    return data;
  };

  const resetPassword = async (token, new_password) => {
    const { data } = await api.post("/auth/password/reset", { token, new_password });
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("axiony_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("axiony_token");
    setUser(null);
  };

  const switchRole = async (role) => {
    const { data } = await api.post("/auth/switch-role", { role });
    setUser((u) => (u ? { ...u, role: data.role } : u));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, resendOtp, forgotPassword, resetPassword, register, logout, switchRole, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
